'use client';

/**
 * The `valueConfirmation` field's editor — **one popover holding the whole
 * question** (VALE-355, `components/attributes/CLAUDE.md` §3).
 *
 * *Is this figure confirmed, against which document, with what note.* Three facts
 * that are useless apart, so they open together and commit together, the way
 * `address` opens five parts of one address instead of spending five rows on them.
 *
 * ## Anatomy, and why each piece is where it is
 *
 * · **The provenance line first, and read-only.** Who confirmed the figure and
 *   when is the thing a reader opening this row most often came to check, and it is
 *   not editable by anyone — the API stamps it. Putting it above the controls
 *   answers the question without the reader touching anything.
 * · **`Confirmed` is the treatment's own segmented boolean**, not a checkbox — the
 *   same control the rows around it use for `Non-estate asset`, and the same
 *   argument [ATTR-8] made: an unticked box cannot distinguish *no* from *nobody
 *   answered*, and on a figure's provenance that ambiguity is the whole point.
 * · **The two source fields are revealed by the answer, not disabled by it.** A
 *   greyed-out control still occupies the eye and still asks to be clicked; a
 *   revealed one arrives when it becomes relevant. `Yes` is what makes them appear,
 *   which is the flag-off form's behaviour, kept.
 * · **`Remove confirmation` only when there is one.** It is the same write as
 *   answering `No`, offered where a reader looking to undo will look — at the foot,
 *   quiet, and absent on a figure that was never confirmed.
 * · **Changing the figure answers `No` for the reader** (VALE-473). A source that
 *   vouched for the old number does not vouch for the new one, so the control
 *   flips where they can see it rather than the API re-stamping the old statement
 *   under their name. {@link withdrawProvenanceIfFigureMoved} holds the rule and
 *   the two cases it deliberately keeps out of.
 *
 * ## Commit, revert, and the one refusal
 *
 * Committing on close is this package's popover contract (`address`, `name`,
 * `multiSelect`, `principalAndMembers` all do it) and `revertedRef` is the same
 * `Escape` guard they all carry: Radix fires `onOpenChange(false)` after
 * `onEscapeKeyDown` for one keypress, so without it a revert would be immediately
 * followed by a commit of the reverted value.
 *
 * **`Yes` against nothing is refused by the spec, not by the server.** The backend
 * rejects a confirmation carrying neither note nor document, so the spec's
 * `validate` catches it and says which of the two is missing — an error the reader
 * can act on instead of a 400 they cannot.
 */

import { useRef } from 'react';
import { Wand2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useCurrencyInput } from '@/components/inventory/inventory-form-card/shared/currency-input';
import { formatCurrency } from '@/lib/formatters/australian-formatters';
import { AttributeSegmentedBoolean } from './attribute-segmented-boolean';
import {
  ATTRIBUTE_COMPOSITE_PART_CONTROL,
  ATTRIBUTE_COMPOSITE_PARTS,
  ATTRIBUTE_EDITOR_BARE,
  ATTRIBUTE_INSET,
  ATTRIBUTE_LABEL_TEXT,
  ATTRIBUTE_LEADING,
  ATTRIBUTE_MUTED_INK,
  ATTRIBUTE_POPOVER_COLLISION_PADDING,
  ATTRIBUTE_POPOVER_PANEL,
  ATTRIBUTE_ROW_ERROR,
  ATTRIBUTE_VALUE_TEXT
} from './attribute-tokens';
import {
  ATTRIBUTE_NOT_RECORDED,
  attributePlaceholder
} from './attribute-placeholder';
import {
  AttributeCompositeField,
  AttributeCompositeMoneyField
} from './attribute-composite-fields';
import type {
  AttributeDraft,
  AttributeOption,
  AttributeValueConfirmation
} from './attribute-types';
import { useAttributePopoverCommit } from './use-attribute-popover-commit';

/** A `SelectItem` cannot hold an empty value; this is "no document linked". */
const NO_DOCUMENT = '__none';

/** Empty is every part absent, never `null` — a half-filled draft needs somewhere to live. */
const EMPTY: AttributeValueConfirmation = {
  amount: '',
  confirmed: false,
  sourceFileId: null,
  note: ''
};

/**
 * **The row's read-out — the figure, then whatever vouches for it.**
 *
 * Its own function because two shells now render this popover and only one of
 * them shows a summary: the grid's trigger *is* the read-out it replaced, while
 * the inventory list keeps its own right-aligned amount cell (VALE-1032). One
 * definition either way, so a `·`-joined summary can never mean two things.
 *
 * **Exported for the row's commit bridge** (VALE-1773) — see
 * `attribute-draft-display.ts`, which renders this same output in the read-out
 * while the host's record prop catches up.
 */
export function valueConfirmationSummary(
  value: AttributeValueConfirmation | null,
  documents: AttributeOption[]
): string | null {
  // `?? EMPTY` here rather than at each caller, so the empty shape has one
  // definition — the bridge would otherwise have to restate it (VALE-1773).
  const draft = value ?? EMPTY;
  const documentLabel = (id: string) =>
    documents.find((option) => option.value === id)?.label ?? id;

  const amountText = draft.amount.trim()
    ? formatCurrency(Number(draft.amount))
    : '';
  const sourceText = draft.confirmed
    ? (draft.sourceFileId
        ? documentLabel(draft.sourceFileId)
        : draft.note.trim()) || 'Confirmed'
    : '';
  return [amountText, sourceText].filter(Boolean).join(' · ') || null;
}

/**
 * **The popover's whole content, without the popover** (VALE-1032) — the amount
 * (typed, or totalled from its parts), the `Confirmed` answer, the document, the
 * note, and any refusal.
 *
 * Extracted so the inventory list can render the identical question anchored to
 * its own cell. The two shells differ only in what the panel hangs off: a grid
 * row replaces its read-out with a `PopoverTrigger`, while a list cell keeps the
 * control it already has and anchors to it (`PopoverAnchor`, which this codebase
 * documents for exactly that). Everything a reader answers lives here, so the
 * question cannot drift between the surface they opened it from.
 *
 * The commit-on-close contract and the `Escape` revert stay with each shell:
 * they are statements about a popover, and the draft they act on is the caller's
 * already.
 */
export function AttributeValueConfirmationFields({
  amountLabel,
  signed = false,
  value,
  documents,
  documentsPlaceholder = 'No documents on this matter yet',
  provenance,
  provenanceTone = 'muted',
  quickFill,
  components,
  deriveAmount,
  derivedAmountCaption,
  error,
  onDraft
}: {
  amountLabel: string;
  signed?: boolean;
  value: AttributeValueConfirmation | null;
  documents: AttributeOption[];
  documentsPlaceholder?: string;
  provenance?: string | null;
  provenanceTone?: 'muted' | 'ai';
  quickFill?: { label: string; amount: number } | null;
  components?: Array<{ key: string; label: string }>;
  deriveAmount?: (values: Record<string, string>) => number | undefined;
  derivedAmountCaption?: string;
  /**
   * **A refused commit, reported inside the popover** (VALE-1032).
   *
   * The grid renders a refusal on the row beneath the value
   * (`attribute-grid.tsx`), which is where a reader on a drawer is already
   * looking, so it passes nothing here. A host whose row is one cell of a dense
   * table has no such line to spare — the inventory list's value cell shares its
   * row with six others and is right-aligned to a fixed width — so it reports the
   * same `useAttributeCommit` message in the one place certainly big enough for
   * it: the popover the reader still has open.
   */
  error?: string | null;
  onDraft: (next: AttributeDraft) => void;
}) {
  const draft = value ?? EMPTY;
  /*
    **The state the popover opened with**, which on this path IS the persisted
    record: the editor is mounted only while its row is open (`attribute-grid`'s
    `openIndex === index`), so the first render's draft came straight from the
    spec. That is what makes a ref the right home for the baseline rather than a
    prop every caller has to thread through.
  */
  const openedWithRef = useRef(draft);
  /*
    Whether the reader has answered the confirmation question themselves this
    session — see {@link withdrawProvenanceIfFigureMoved}, which stops offering
    an opinion once they have.
  */
  const answeredRef = useRef(false);
  /*
    `value` only — the hook's own `onChange` hands back a *parsed* number, and this
    control moves the raw string around so a decimal survives mid-typing. Same
    reasoning as `AttributeMoneyEditor`'s, which is where this pattern is documented.
  */
  const currencyInput = useCurrencyInput({ value: draft.amount });

  /** Two raw amount drafts holding the same figure — `5000` vs `5000.00`. */
  const sameFigure = (a: string, b: string) => {
    const [x, y] = [a.trim(), b.trim()];
    if (x === y) return true;
    const [nx, ny] = [Number(x), Number(y)];
    return x !== '' && y !== '' && Number.isFinite(nx) && Number.isFinite(ny)
      ? nx === ny
      : false;
  };

  /**
   * **Changing the figure withdraws the provenance that vouched for it**
   * (VALE-473, restoring LF-4356's rule to this surface).
   *
   * `attribute-types.ts` already gave this as the reason the figure and its
   * source share one popover — *"changing a figure nulls the confirmation that
   * vouched for the old one, and if that happens in the same popover as the
   * change, the reader can see it and re-state the source immediately"*. The row
   * shipped doing the opposite: the draft arrives pre-populated with the existing
   * source, so a figure-only edit still sent an explicit set-intent, and the API
   * re-stamped a statement that never showed the new number — with today's date
   * and the reader's name on it.
   *
   * So the answer flips itself to `No`, in front of the reader, and a `No`
   * commits the un-confirm intent. Two deliberate narrowings:
   *
   * · **Only while the provenance is still the one that arrived, and only until
   *   the reader answers for themselves.** Editing the note, picking a different
   *   document, or touching the `Yes`/`No` control is them re-stating the source
   *   for the new figure; flipping their own answer out from under them as they
   *   kept typing would be the more surprising behaviour of the two.
   * · **The source stays in the draft**, unlike answering `No` by hand — that
   *   answer withdraws the provenance deliberately, this one only observes that
   *   the figure moved. A reader who does mean *"the statement says 6,100 too"*
   *   gets it back in one click with the source still in front of them, and
   *   nothing stale can reach the record either way.
   */
  const withdrawProvenanceIfFigureMoved = (
    next: AttributeValueConfirmation
  ): AttributeValueConfirmation => {
    const opened = openedWithRef.current;
    if (answeredRef.current) return next;
    if (!opened.confirmed || !next.confirmed) return next;
    if (next.note !== opened.note || next.sourceFileId !== opened.sourceFileId)
      return next;
    return sameFigure(next.amount, opened.amount)
      ? next
      : { ...next, confirmed: false };
  };

  const hasComponents = Boolean(components?.length);

  /**
   * A component's new draft, with `amount` recomputed from the result (VALE-967).
   *
   * Writing the running total back into `amount` is what keeps this widening
   * invisible to everything else: the summary, `withdrawProvenanceIfFigureMoved`
   * and the spec's `validate` / `toPatch` all read one figure, exactly as they did
   * when it was typed. `deriveAmount` returning `undefined` (nothing recorded yet)
   * empties it, so an all-blank breakdown reads as no figure rather than `$0.00`.
   */
  const withComponent = (
    key: string,
    raw: string
  ): AttributeValueConfirmation => {
    const nextComponents = { ...(draft.components ?? {}), [key]: raw };
    const derived = deriveAmount?.(nextComponents);
    return withdrawProvenanceIfFigureMoved({
      ...draft,
      components: nextComponents,
      amount: derived === undefined ? '' : String(derived)
    });
  };

  const amountText = draft.amount.trim()
    ? formatCurrency(Number(draft.amount))
    : '';

  return (
    <div className={ATTRIBUTE_COMPOSITE_PARTS}>
      {/*
            **Only while the answer below it is `Yes`** (VALE-473). This line
            describes the confirmation the record holds, so above a `No` it
            contradicts the control it is meant to qualify — *"Confirmed by J.
            Smith on 29 July · ANZ statement"* sitting on top of *"Confirmed
            against a source: No"*. It was already reachable by answering `No` by
            hand; the figure now answering `No` on the reader's behalf made it the
            common case rather than the rare one.

            Hidden rather than reworded, because the provenance is one click away
            in both directions: answering `Yes` brings back this line *and* the
            note it names.
          */}
      {provenance && draft.confirmed ? (
        <p
          className={cn(
            ATTRIBUTE_LABEL_TEXT,
            // `ai` is the theme's purple for "a machine produced this" — see the
            // `provenanceTone` prop. Muted is the human default.
            provenanceTone === 'ai'
              ? 'text-ai-subtle-foreground'
              : ATTRIBUTE_MUTED_INK
          )}
        >
          {provenance}
        </p>
      ) : null}

      {/*
            **The figure first.** It is what the row is named for and what a reader
            opening this popover most often came to change; provenance qualifies it.
            Bound to `useCurrencyInput` rather than re-typed, so the `$`, the
            thousands separators and the raw-string-while-focused contract are the
            ones every other money field on the surface already has.
          */}
      <AttributeCompositeField label={amountLabel}>
        {hasComponents ? (
          /*
                **The running total of the fields directly below it** (VALE-967).
                Not a locked field: the parts that decide it are in this same
                popover, so the figure reads as the sum it is and there is
                nothing for a reader to go looking for elsewhere. That is the
                whole reason the parts were folded in here rather than given
                their own section — a total the reader could not edit, sitting
                above the section that decided it, still left two places
                answering one question.
              */
          <>
            <p
              className={cn(ATTRIBUTE_VALUE_TEXT, ATTRIBUTE_LEADING, 'py-0.5')}
            >
              {amountText || ATTRIBUTE_NOT_RECORDED}
            </p>
            {derivedAmountCaption ? (
              <span className={cn(ATTRIBUTE_LABEL_TEXT, ATTRIBUTE_MUTED_INK)}>
                {derivedAmountCaption}
              </span>
            ) : null}
          </>
        ) : (
          <div className="relative flex items-center">
            <span
              aria-hidden
              className={cn(
                ATTRIBUTE_VALUE_TEXT,
                'text-muted-foreground pointer-events-none absolute left-2'
              )}
            >
              $
            </span>
            <Input
              autoFocus
              aria-label={amountLabel}
              type="text"
              inputMode="decimal"
              value={currencyInput.displayValue}
              onFocus={(event) => {
                // Unformat first, then select on the next frame: focusing swaps "1,500.00" for "1500",
                // and replacing the value drops a selection made before it, so the figure opened
                // focused but not selected and typing appended instead of overriding.
                const input = event.currentTarget;
                // Typed before the frame came round: selecting then would swallow those keystrokes.
                delete input.dataset.typed;
                currencyInput.handleFocus();
                requestAnimationFrame(() => {
                  if (
                    document.activeElement === input &&
                    !input.dataset.typed
                  ) {
                    input.select();
                  }
                });
              }}
              onChange={(event) => {
                event.currentTarget.dataset.typed = '1';
                currencyInput.handleChange(event.target.value);
                onDraft(
                  withdrawProvenanceIfFigureMoved({
                    ...draft,
                    amount: event.target.value
                  })
                );
              }}
              onBlur={currencyInput.handleBlur}
              className={cn(ATTRIBUTE_COMPOSITE_PART_CONTROL, 'pl-5')}
            />
          </div>
        )}
        {/*
              **The figure this one is usually copied from, as one click**
              (VALE-1032). It came from the inventory list's quick-set dialog and
              moved in here when the popover replaced that dialog, so the offer is
              made on whichever surface the reader opened the figure from.

              Hidden once the amount already holds it — including while they are
              mid-type, which is why it is compared against the live draft and not
              against what the popover opened with. `sameFigure` rather than a
              string test, so `5000` and `5000.00` are the one figure they read as.

              Never on a composed row: there the amount is the total of the parts
              below, so a value handed straight to it would be overwritten by the
              next keystroke in either part.
            */}
        {quickFill &&
        !hasComponents &&
        !sameFigure(draft.amount, String(quickFill.amount)) ? (
          <button
            type="button"
            onClick={() =>
              onDraft(
                withdrawProvenanceIfFigureMoved({
                  ...draft,
                  amount: String(quickFill.amount)
                })
              )
            }
            /*
                  `hover:underline` is this app's own idiom for a bare text button
                  with no other hover affordance, and `--info-subtle-foreground`
                  already holds what the dialog's `hover:text-blue-700` escalated
                  to — both carried over verbatim from the dialog rather than
                  re-picked (VALE-529 settled the same shape).
                */
            /*
              `items-start` + `text-left`, not the dialog's centred `items-center`:
              this panel is 272px and the label wraps at a real figure, where
              centring left the glyph floating beside a two-line block and the
              second line adrift under the first.
            */
            className={cn(
              ATTRIBUTE_LABEL_TEXT,
              'text-info-subtle-foreground mt-0.5 flex items-start gap-1 text-left font-medium hover:underline'
            )}
          >
            <Wand2 className="mt-[0.15em] h-3 w-3 shrink-0" />
            <span>
              Use {quickFill.label} ({formatCurrency(quickFill.amount)})
            </span>
          </button>
        ) : null}
        {signed && !hasComponents ? (
          <span className={cn(ATTRIBUTE_LABEL_TEXT, ATTRIBUTE_MUTED_INK)}>
            A negative balance is recorded as a credit.
          </span>
        ) : null}
      </AttributeCompositeField>

      {/*
            The parts, directly under the total they add up to. Editing one
            recomputes `amount` in the same draft, so everything downstream — the
            summary line, the provenance withdrawal, the spec's own `validate` and
            `toPatch` — keeps reading one figure and never learns it was composed.
          */}
      {components?.map(({ key, label }, index) => (
        <AttributeCompositeMoneyField
          key={key}
          label={label}
          value={draft.components?.[key] ?? ''}
          autoFocus={index === 0}
          onChange={(raw) => onDraft(withComponent(key, raw))}
        />
      ))}

      <AttributeCompositeField label="Confirmed against a source">
        <AttributeSegmentedBoolean
          value={draft.confirmed}
          ariaLabel="Confirmed against a source"
          onValueChange={(next) => {
            // Their answer, so this control stops offering one — see
            // `withdrawProvenanceIfFigureMoved`.
            answeredRef.current = true;
            /*
                  Answering `No` drops the source with it. Keeping a note beside a
                  `No` would leave the record holding provenance for a figure it
                  says is unconfirmed, and the next `Yes` would silently re-confirm
                  against a source the reader had already walked away from.
                */
            onDraft(
              next
                ? { ...draft, confirmed: true }
                : {
                    // The amount survives — answering `No` withdraws the
                    // provenance, not the figure.
                    amount: draft.amount,
                    confirmed: false,
                    sourceFileId: null,
                    note: ''
                  }
            );
          }}
        />
      </AttributeCompositeField>

      {draft.confirmed ? (
        <>
          <AttributeCompositeField label="Document">
            {/*
                  **A `Select`, not a `Command` list** (VALE-355 round 4).

                  The list was a cmdk `Command` and it would not scroll inside the
                  popover — the sixth document was clipped with no way to reach the
                  rest, which on a matter with a real document set makes the picker
                  useless. Rather than debug cmdk's scroll container inside a Radix
                  popover, this is the control the flag-off form already uses for
                  exactly this job (`ValueSourceSelectContent`): a `Select` scrolls
                  natively, brings its own keyboard handling, and is one fewer
                  bespoke thing on the surface.

                  `NO_DOCUMENT` is the sentinel a `SelectItem` needs because it
                  cannot hold an empty value — the same `__none` shape this codebase
                  already ships for a nullable select. It is what lets a reader keep
                  the confirmation but drop the document and rely on the note.
                */}
            <Select
              value={draft.sourceFileId ?? NO_DOCUMENT}
              onValueChange={(next) =>
                onDraft({
                  ...draft,
                  sourceFileId: next === NO_DOCUMENT ? null : next
                })
              }
              disabled={documents.length === 0}
            >
              <SelectTrigger className={ATTRIBUTE_COMPOSITE_PART_CONTROL}>
                <SelectValue
                  placeholder={
                    documents.length === 0
                      ? documentsPlaceholder
                      : 'Link a document (optional)'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_DOCUMENT}>No document</SelectItem>
                {documents.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </AttributeCompositeField>

          <AttributeCompositeField label="Note">
            <Input
              value={draft.note}
              placeholder="Name what the figure was read off…"
              onChange={(event) =>
                onDraft({ ...draft, note: event.target.value })
              }
              className={ATTRIBUTE_COMPOSITE_PART_CONTROL}
            />
          </AttributeCompositeField>

          <p className={cn(ATTRIBUTE_LABEL_TEXT, ATTRIBUTE_MUTED_INK)}>
            Both optional. Record a source if you have one.
          </p>
        </>
      ) : null}

      {/*
            **The refusal, for a host with nowhere else to put it** (VALE-1032) —
            see the `error` prop. `role="status"` and not `alert`, and the same
            copy the commit path produced, for the reasons `attribute-grid.tsx`
            gives where it renders the identical line on a row.
          */}
      {error ? (
        <p role="status" className={ATTRIBUTE_ROW_ERROR}>
          {error}
        </p>
      ) : null}
    </div>
  );
}

export function AttributeValueConfirmationEditor({
  label,
  amountLabel,
  signed = false,
  value,
  documents,
  documentsPlaceholder = 'No documents on this matter yet',
  provenance,
  provenanceTone,
  quickFill,
  components,
  deriveAmount,
  derivedAmountCaption,
  error,
  onDraft,
  onRevert,
  onCommit
}: {
  label: string;
  onRevert: () => void;
  onCommit: (value: AttributeDraft) => void;
  amountLabel: string;
  signed?: boolean;
  value: AttributeValueConfirmation | null;
  documents: AttributeOption[];
  documentsPlaceholder?: string;
  provenance?: string | null;
  provenanceTone?: 'muted' | 'ai';
  quickFill?: { label: string; amount: number } | null;
  components?: Array<{ key: string; label: string }>;
  deriveAmount?: (values: Record<string, string>) => number | undefined;
  derivedAmountCaption?: string;
  /**
   * **A refused commit, reported inside the popover** (VALE-1032).
   *
   * The grid renders a refusal on the row beneath the value
   * (`attribute-grid.tsx`), which is where a reader on a drawer is already
   * looking, so it passes nothing here. A host whose row is one cell of a dense
   * table has no such line to spare — the inventory list's value cell shares its
   * row with six others and is right-aligned to a fixed width — so it reports the
   * same `useAttributeCommit` message in the one place certainly big enough for
   * it: the popover the reader still has open.
   */
  error?: string | null;
  onDraft: (next: AttributeDraft) => void;
}) {
  const draft = value ?? EMPTY;
  /*
    `Enter` commits and closes, `Escape` reverts, dismissing commits: the one
    contract all seven composite popover editors share (VALE-1321). See
    `./use-attribute-popover-commit`, which owns every subtlety in it.
  */
  const popoverCommit = useAttributePopoverCommit({
    draft: draft,
    onRevert,
    onCommit
  });

  const summary = valueConfirmationSummary(draft, documents);

  return (
    <Popover {...popoverCommit.popover}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={label}
          className={cn(
            ATTRIBUTE_VALUE_TEXT,
            ATTRIBUTE_INSET,
            ATTRIBUTE_LEADING,
            'text-foreground min-w-0 flex-1 truncate rounded-md text-left',
            ATTRIBUTE_EDITOR_BARE
          )}
        >
          {summary ?? attributePlaceholder(label)}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        collisionPadding={ATTRIBUTE_POPOVER_COLLISION_PADDING}
        /*
          The shared composite panel (VALE-863), width AND padding, bound
          rather than restated as `w-80 p-0` with the padding re-typed inside
          the fields (VALE-1505). The inventory list's own shell for these
          same fields binds the same token.
        */
        /*
          {@link ATTRIBUTE_COMPOSITE_PARTS} joins the panel at VALE-1710. The
          fields component below already IS that column, so this class did
          nothing while it was the only child — and has to be here now that the
          confirm row is a second one, or the gap above the control would be the
          panel's zero rather than the 6.8px every other composite panel sets it
          at.
        */
        className={cn(ATTRIBUTE_POPOVER_PANEL, ATTRIBUTE_COMPOSITE_PARTS)}
        {...popoverCommit.content}
      >
        <AttributeValueConfirmationFields
          amountLabel={amountLabel}
          signed={signed}
          value={value}
          documents={documents}
          documentsPlaceholder={documentsPlaceholder}
          provenance={provenance}
          provenanceTone={provenanceTone}
          quickFill={quickFill}
          components={components}
          deriveAmount={deriveAmount}
          derivedAmountCaption={derivedAmountCaption}
          error={error}
          onDraft={onDraft}
        />
        {/*
          **No "Remove confirmation" action** (VALE-355 round 4). It was here and it
          was redundant: answering `Confirmed against a source — No` is the same
          write, in the field that already asks the question. Two controls for one
          decision is what made the row confusing in its first shape, and it would
          have been the same mistake one level in.
        */}
        {popoverCommit.confirm}
      </PopoverContent>
    </Popover>
  );
}
